import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import ReactDataGrid, { Column, SortColumn } from "react-data-grid";
import { Localization } from "@viamap/viamap2-common";
import { GlassButton, GlassButtonRow } from "src/components/GlassButtons";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { PLAElementRecord, PLAElementTypeDefinition, PLAProject } from "./PLAState";
import { getPLAAlertDetails, resolvePLACalculatedAttributes } from "./PLAExpression";

export type PLATableRef = {
   scrollToRow: (elementId: string, focusRow?: boolean) => void;
};

type PLATableRow = PLAElementRecord & {
   __alerts: string[];
   __alertsByAttributeId: Record<string, string[]>;
   __calculated: Record<string, any>;
};

type Props = {
   project: PLAProject;
   elementTypeId: string;
   elementType: PLAElementTypeDefinition;
   elements: PLAElementRecord[];
   allElements: PLAElementRecord[];
   relations: any[];
   selectedElementId?: string;
   readOnly: boolean;
   onRowClick: (element: PLAElementRecord) => void;
   onAddElement: () => void;
   onOpenElement: (element: PLAElementRecord) => void;
   onCreateRelation: (element: PLAElementRecord) => void;
   onDeleteElement: (element: PLAElementRecord) => void;
};

function compareValues(a: any, b: any) {
   if (a === b) return 0;
   if (a === undefined || a === null) return -1;
   if (b === undefined || b === null) return 1;
   if (typeof a === "number" && typeof b === "number") return a - b;
   return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

function getAttributeDisplay(row: PLATableRow, attributeId: string) {
   if (Object.prototype.hasOwnProperty.call(row.__calculated, attributeId)) return row.__calculated[attributeId];
   const value = row.attributes[attributeId];
   return value?.mode === "constant" ? value.value : value?.expression || "";
}

function RowActionButton(props: { title: string; icon: "edit" | "mapLink" | "delete"; onClick: () => void }) {
   return (
      <GlassButton
         title={props.title}
         aria-label={props.title}
         onClick={(event) => {
            event.stopPropagation();
            props.onClick();
         }}
         style={{ width: 30, minWidth: 30, height: 28, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
      >
         <MIBIcon icon={props.icon} />
      </GlassButton>
   );
}

function initialVisibleColumns(elementType: PLAElementTypeDefinition): Record<string, boolean> {
   const visibleColumns: Record<string, boolean> = { name: true };
   Object.entries(elementType.attributes || {}).forEach(([attributeId, definition]) => {
      if (isElementNameAttribute(attributeId, definition.name)) return;
      visibleColumns[`attr:${attributeId}`] = !definition.hideAsDefault;
   });
   return visibleColumns;
}

function isElementNameAttribute(attributeId: string, attributeName?: string) {
   const reservedNames = new Set(["name", Localization.getText("Name").trim().toLowerCase()]);
   return reservedNames.has(attributeId.trim().toLowerCase()) || reservedNames.has((attributeName || "").trim().toLowerCase());
}

export const PLATable = forwardRef<PLATableRef, Props>((props, ref) => {
   const containerRef = useRef<HTMLDivElement | null>(null);
   const gridRef = useRef<any>(null);
   const [filter, setFilter] = useState("");
   const [sortColumns, setSortColumns] = useState<readonly SortColumn[]>([]);
   const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(() => initialVisibleColumns(props.elementType));

   useEffect(() => {
      setVisibleColumns(initialVisibleColumns(props.elementType));
   }, [props.elementTypeId, props.elementType]);

   const rows: PLATableRow[] = useMemo(() => {
      return props.elements.map((element) => {
         let calculated: Record<string, any> = {};
         let alerts: string[] = [];
         let alertsByAttributeId: Record<string, string[]> = {};
         try {
            calculated = resolvePLACalculatedAttributes(element, props.project, props.allElements, props.relations);
            const alertDetails = getPLAAlertDetails(element, props.project, props.allElements, props.relations);
            alerts = alertDetails.map((alert) => alert.message);
            alertsByAttributeId = alertDetails.reduce<Record<string, string[]>>((acc, alert) => {
               acc[alert.attributeId] = [...(acc[alert.attributeId] || []), alert.message];
               return acc;
            }, {});
         } catch (error: any) {
            alerts = [error.message || String(error)];
         }
         return { ...element, __alerts: alerts, __alertsByAttributeId: alertsByAttributeId, __calculated: calculated };
      });
   }, [props.elements, props.project, props.allElements, props.relations]);

   const filteredRows = useMemo(() => {
      const lowerFilter = filter.trim().toLowerCase();
      const visible = lowerFilter
         ? rows.filter((row) => JSON.stringify({ name: row.name, attributes: row.attributes, calculated: row.__calculated }).toLowerCase().includes(lowerFilter))
         : rows;
      if (sortColumns.length === 0) return visible;
      return [...visible].sort((a, b) => {
         for (const sort of sortColumns) {
            const aValue = sort.columnKey.startsWith("attr:") ? getAttributeDisplay(a, sort.columnKey.slice(5)) : (a as any)[sort.columnKey];
            const bValue = sort.columnKey.startsWith("attr:") ? getAttributeDisplay(b, sort.columnKey.slice(5)) : (b as any)[sort.columnKey];
            const result = compareValues(aValue, bValue);
            if (result !== 0) return sort.direction === "ASC" ? result : -result;
         }
         return 0;
      });
   }, [rows, filter, sortColumns]);

   const focusSelectedRow = useCallback(() => {
      window.setTimeout(() => {
         const selectedCell = containerRef.current?.querySelector<HTMLElement>(".rdg-row-selected .rdg-cell");
         if (!selectedCell) return;
         selectedCell.tabIndex = -1;
         selectedCell.focus();
      }, 0);
   }, []);

   const scrollToRow = useCallback((elementId: string, focusRow = false) => {
      const rowIndex = filteredRows.findIndex((row) => row.id === elementId);
      if (rowIndex >= 0) {
         gridRef.current?.scrollToCell?.({ rowIdx: rowIndex, idx: 0 });
         if (focusRow) focusSelectedRow();
      }
   }, [filteredRows, focusSelectedRow]);

   useImperativeHandle(ref, () => ({ scrollToRow }), [scrollToRow]);

   useEffect(() => {
      if (props.selectedElementId) scrollToRow(props.selectedElementId, true);
   }, [props.selectedElementId, scrollToRow]);

   const columns: Column<PLATableRow>[] = useMemo(() => {
      const attributeColumns: Column<PLATableRow>[] = Object.entries(props.elementType.attributes || {})
         .filter(([attributeId, definition]) => !isElementNameAttribute(attributeId, definition.name))
         .map(([attributeId, definition]) => ({
            key: `attr:${attributeId}`,
            name: definition.name || attributeId,
            sortable: true,
            resizable: true,
            renderCell: ({ row }) => {
               const alertMessages = row.__alertsByAttributeId[attributeId] || [];
               return <span title={alertMessages.join("\n") || undefined} style={alertMessages.length ? { display: "block", width: "100%", height: "100%", background: "#f8d7da", color: "#842029", padding: "0 4px" } : undefined}>{String(getAttributeDisplay(row, attributeId) ?? "")}</span>;
            }
         }));
      return [
         {
            key: "alert",
            name: "",
            width: 42,
            renderCell: ({ row }) => row.__alerts.length ? <span title={row.__alerts.join("\n")} style={{ color: "#d71920", fontWeight: 700 }}>!</span> : null
         },
         ...(visibleColumns.name ? [{ key: "name", name: Localization.getText("Name"), sortable: true, resizable: true } as Column<PLATableRow>] : []),
         ...attributeColumns.filter((column) => visibleColumns[column.key] !== false),
         {
            key: "actions",
            name: Localization.getText("Actions"),
            width: props.readOnly ? 54 : 120,
            renderCell: ({ row }) => (
               <GlassButtonRow>
                  <RowActionButton title={Localization.getText("Open element")} icon="edit" onClick={() => props.onOpenElement(row)} />
                  {!props.readOnly && <RowActionButton title={Localization.getText("Create relation")} icon="mapLink" onClick={() => props.onCreateRelation(row)} />}
                  {!props.readOnly && <RowActionButton title={Localization.getText("Delete element")} icon="delete" onClick={() => props.onDeleteElement(row)} />}
               </GlassButtonRow>
            )
         }
      ];
   }, [props.elementType, props.readOnly, props.onOpenElement, props.onCreateRelation, props.onDeleteElement, visibleColumns]);

   const columnOptions = useMemo(() => [
      { key: "name", name: Localization.getText("Name") },
      ...Object.entries(props.elementType.attributes || {})
         .filter(([attributeId, definition]) => !isElementNameAttribute(attributeId, definition.name))
         .map(([attributeId, definition]) => ({ key: `attr:${attributeId}`, name: definition.name || attributeId }))
   ], [props.elementType]);

   return (
      <div ref={containerRef} style={{ display: "flex", flexDirection: "column", gap: 8, minHeight: 360 }} data-testid="pla-table">
         <GlassButtonRow>
            {!props.readOnly && <GlassButton onClick={props.onAddElement}>{Localization.getText("Add element")}</GlassButton>}
            <details style={{ marginLeft: props.readOnly ? 0 : undefined, position: "relative" }}>
               <summary>{Localization.getText("Columns")}</summary>
               <div style={{ position: "absolute", zIndex: 3, display: "flex", flexDirection: "column", gap: 4, padding: 8, background: "#fff", color: "#222", border: "1px solid #c8c8c8", boxShadow: "0 2px 8px rgba(0,0,0,.18)" }}>
                  {columnOptions.map((column) => (
                     <label key={column.key} style={{ display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap", color: "#222" }}>
                        <input
                           type="checkbox"
                           checked={visibleColumns[column.key] !== false}
                           onChange={(event) => setVisibleColumns((prev) => ({ ...prev, [column.key]: event.target.checked }))}
                        />
                        {column.name}
                     </label>
                  ))}
               </div>
            </details>
            <input
               aria-label={Localization.getText("Filter")}
               value={filter}
               onChange={(event) => setFilter(event.target.value)}
               placeholder={Localization.getText("Filter")}
               style={{ marginLeft: "auto", minWidth: 220 }}
            />
         </GlassButtonRow>
         <ReactDataGrid
            ref={gridRef}
            columns={columns}
            rows={filteredRows}
            className="rdg-light"
            sortColumns={sortColumns}
            onSortColumnsChange={setSortColumns}
            rowKeyGetter={(row) => row.id}
            rowClass={(row) => row.id === props.selectedElementId ? "rdg-row-selected" : undefined}
            onCellClick={({ row }) => props.onRowClick(row)}
            style={{ minHeight: 320 }}
         />
      </div>
   );
});
